import * as _ from "lodash";
import { ComponentProps } from "react";
import { useLocation } from "react-router-dom";

import { Enterprise } from "@ctra/api";
import { Steps } from "@ctra/components";
import { FormikProps } from "formik";

const { Step } = Steps;

/**
 * "wait" | "process" | "finish" | "error"
 */
type Status = ComponentProps<typeof Step>["status"];

type StepDefinition<R> = {
  key: Exclude<keyof R, "index">;
  path: string;
};

type API<R, V> = [
  {
    steps: Array<StepDefinition<R>>;
    currentStep: number;
  },
  {
    previous: () => void;
    next: () => void;
    jump: (to: number) => void;
    getStatus: (path: R[keyof R], formikProps: FormikProps<V>) => Status;
  }
];

/**
 * Make an API for navigating through multi-step wizards
 * @param paths
 * @param order
 * @param validationGroups
 */
export const useSteps = <R extends Record<string, unknown>, V>(
  paths: R,
  order: Array<Exclude<keyof R, "index">>,
  validationGroups: Record<keyof R, Array<keyof V>>
): API<R, V> => {
  const location = useLocation();

  /**
   * Filter out all the nested paths
   */
  const rootLevelPaths = _.pickBy(paths, _.isString);

  /**
   * Make an internal, ordered structure from the given paths
   */
  const steps: Array<StepDefinition<R>> = order.map((key) => ({
    path: rootLevelPaths[key as string],
    key
  }));

  /**
   * Resolve the Step component status based on the order of pages in the wizard
   * @param path
   * @param errors
   * @param touched
   */
  const getStatus = (path: R[keyof R], { errors, touched }: FormikProps<V>): Status => {
    let status: Status = "process";

    /**
     * Get the index of the current step
     */
    const currentIdx = _.findIndex(steps, ["path", location.pathname]);

    /**
     * Get the index of the given path
     */
    const pathIdx = _.findIndex(steps, ["path", path]);

    /**
     * Resolve the route of the given path
     */
    const routeOfPath = steps[pathIdx].key;

    /**
     * Pick the validating fields which have been touched
     * and present on the current path
     */
    const mayThrowError = _.pick(touched, validationGroups[routeOfPath]);

    /**
     * Process errors by removing undefined values from the field arrays
     */
    const processedErrors = _.omitBy(
      errors,
      (error) => !error || (_.isArray(error) && _.isEmpty(_.compact(error)))
    );

    /**
     * Keep the errors only from fields which are
     * relevant for the current path
     */
    const touchedFieldErrors = _.pick(processedErrors, _.keys(mayThrowError));

    /**
     * Tell if there is an error in the route
     */
    const errorInRoute =
      _.intersection(validationGroups[routeOfPath], _.keys(touchedFieldErrors) as Array<keyof V>).length > 0;

    if (!_.isEmpty(processedErrors) && errorInRoute) {
      status = "error";
    } else if (pathIdx > currentIdx) {
      status = "wait";
    } else if (pathIdx < currentIdx) {
      status = "finish";
    }

    return status;
  };

  /**
   * Navigate to the requested step in the wizard
   * @param current
   */
  const jump = (current: number) => {
    if (steps[current]) {
      Enterprise.history.push({
        pathname: steps[current].path,
        search: location.search
      });
    }
  };

  /**
   * Get current step
   */
  const currentStep = _.findIndex(steps, ["path", location.pathname]);

  /**
   * Go to the previous step
   */
  const previous = () => jump(currentStep - 1);

  /**
   * Go to the next step
   */
  const next = () => jump(currentStep + 1);

  return [
    { steps, currentStep },
    { getStatus, previous, next, jump }
  ];
};
